import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { Input } from 'components/Form/Input/Input';
import { AddressAutocomplete } from 'components/OnlineBooking/Walking/Address/AddressAutocomplete';
import { Alert } from 'components/UI/Alert/Alert';
import { Button } from 'components/UI/Button/Button';
import Modal from 'components/UI/Modal/Modal';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { CustomerAddress } from 'types/Customer';
import {
    buildCustomerAddressFromFields,
    buildCustomerAddressFromPlace,
    isSelectedPlaceComplete,
    SelectedPlace,
    WALKING_ADDRESS_DETAILS_ERROR
} from 'actions/onlineBooking/actions/OnlineBookingWalkingAddress';
import classes from './AddressDialog.module.scss';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

interface AddressDialogProps {
    open: boolean;
    editing: boolean;
    saving?: boolean;
    error?: string;
    onClose: () => void;
    onDismissError: () => void;
    onSave: (address: CustomerAddress) => void;
}

const manualLabelProps = { shrink: true as const };

export const AddressDialog: React.FC<AddressDialogProps> = ({
    open,
    editing,
    saving,
    error,
    onClose,
    onDismissError,
    onSave
}) => {
    const marketplace = useMarketplace();
    const [useManualFields, setUseManualFields] = React.useState(!GOOGLE_MAPS_API_KEY);
    const [query, setQuery] = React.useState('');
    const [selectedPlace, setSelectedPlace] = React.useState<SelectedPlace | undefined>();
    const [placeError, setPlaceError] = React.useState(false);
    const [addressLineOne, setAddressLineOne] = React.useState('');
    const [city, setCity] = React.useState('');
    const [state, setState] = React.useState('');
    const [zipCode, setZipCode] = React.useState('');

    React.useEffect(() => {
        if (!open) {
            setQuery('');
            setSelectedPlace(undefined);
            setPlaceError(false);
            setAddressLineOne('');
            setCity('');
            setState('');
            setZipCode('');
            setUseManualFields(!GOOGLE_MAPS_API_KEY);
        }
    }, [open]);

    const country = marketplace.address?.country ?? '';
    const showError = Boolean(error) || placeError;

    const canSave = useManualFields
        ? addressLineOne.trim().length > 0 &&
          city.trim().length > 0 &&
          state.trim().length > 0 &&
          zipCode.trim().length > 0
        : isSelectedPlaceComplete(selectedPlace);

    const dismissError = () => {
        setPlaceError(false);

        if (error) {
            onDismissError();
        }
    };

    const saveHandler = () => {
        if (useManualFields) {
            onSave(
                buildCustomerAddressFromFields({
                    addressLineOne: addressLineOne.trim(),
                    city: city.trim(),
                    state: state.trim(),
                    zipCode: zipCode.trim(),
                    country
                })
            );
            return;
        }

        if (!isSelectedPlaceComplete(selectedPlace)) {
            return;
        }

        onSave(buildCustomerAddressFromPlace(selectedPlace));
    };

    const selectPlaceHandler = (place: SelectedPlace) => {
        dismissError();
        setSelectedPlace(place);
        setQuery(place.formattedAddress);
    };

    const selectFailedHandler = () => {
        dismissError();
        setSelectedPlace(undefined);
        setQuery('');
        setPlaceError(true);
    };

    return (
        <Modal
            open={open}
            title={editing ? 'Change Your Address' : 'Add Your Address'}
            className={classes.root}
            headerClassName={classes.header}
            bodyClassName={classes.body}
            footerClassName={classes.footer}
            footer={
                <>
                    <Button variant="black" disabled={saving} onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        variant="green"
                        disabled={!canSave || saving}
                        loading={saving}
                        onClick={saveHandler}
                    >
                        Save
                    </Button>
                </>
            }
            onClose={onClose}
        >
            <div className={classes.fields}>
                {showError && (
                    <Alert
                        variant="error"
                        icon={faInfoCircle}
                        className={classes.error}
                        text={error || WALKING_ADDRESS_DETAILS_ERROR}
                    />
                )}

                {useManualFields ? (
                    <>
                        <Input
                            label="Home Address"
                            value={addressLineOne}
                            InputLabelProps={manualLabelProps}
                            onChange={event => {
                                dismissError();
                                setAddressLineOne(event.target.value);
                            }}
                        />
                        <div className={classes.row}>
                            <Input
                                label="City"
                                value={city}
                                InputLabelProps={manualLabelProps}
                                onChange={event => {
                                    dismissError();
                                    setCity(event.target.value);
                                }}
                            />
                            <Input
                                label="State"
                                value={state}
                                InputLabelProps={manualLabelProps}
                                onChange={event => {
                                    dismissError();
                                    setState(event.target.value);
                                }}
                            />
                            <Input
                                label="Zip"
                                value={zipCode}
                                InputLabelProps={manualLabelProps}
                                onChange={event => {
                                    dismissError();
                                    setZipCode(event.target.value);
                                }}
                            />
                        </div>
                    </>
                ) : (
                    <AddressAutocomplete
                        value={query}
                        onChange={value => {
                            dismissError();
                            setQuery(value);
                            setSelectedPlace(undefined);
                        }}
                        onSelect={selectPlaceHandler}
                        onSelectFailed={selectFailedHandler}
                        onPlacesUnavailable={() => setUseManualFields(true)}
                    />
                )}
            </div>
        </Modal>
    );
};
